
// Define the translation key type
export type AdminTranslationKey =
  | "admin.title" 
  | "admin.channels" 
  | "admin.users" 
  | "admin.licenses" 
  | "admin.derivatives"
  | "admin.manageDerivatives"
  | "admin.viewDerivative" 
  | "admin.approveDerivative" 
  | "admin.rejectDerivative" 
  | "admin.deleteDerivative" 
  | "admin.confirmDelete" 
  | "admin.usersPanelTitle"
  | "admin.usersPanelDescription"
  | "admin.channelsPanelTitle"
  | "admin.channelsPanelDescription"
  | "admin.licensesPanelTitle"
  | "admin.licensesPanelDescription"
  | "admin.challengesPanelTitle"
  | "admin.challengesPanelDescription"
  | "admin.analyticsPanelTitle"
  | "admin.analyticsPanelDescription"
  | "admin.uploadClip" 
  | "admin.editChannel" 
  | "admin.createChannel" 
  | "admin.channelName" 
  | "admin.channelDescription" 
  | "admin.channelLogo" 
  | "admin.channelWebsite" 
  | "admin.channelCategory" 
  | "admin.saveChanges" 
  | "admin.cancel" 
  | "admin.deleteChannel" 
  | "admin.confirmChannelDeletion" 
  | "admin.userManagement" 
  | "admin.createUser" 
  | "admin.editUser" 
  | "admin.deleteUser" 
  | "admin.confirmUserDeletion" 
  | "admin.userName" 
  | "admin.userEmail" 
  | "admin.userRole" 
  | "admin.saveUser" 
  | "admin.licenseManagement" 
  | "admin.createLicense" 
  | "admin.editLicense" 
  | "admin.deleteLicense" 
  | "admin.confirmLicenseDeletion" 
  | "admin.licenseName" 
  | "admin.licenseDescription" 
  | "admin.licensePrice" 
  | "admin.saveLicense" 
  | "admin.challengeManagement" 
  | "admin.createChallenge" 
  | "admin.editChallenge" 
  | "admin.deleteChallenge" 
  | "admin.confirmChallengeDeletion" 
  | "admin.challengeName" 
  | "admin.challengeDescription" 
  | "admin.challengeStartDate" 
  | "admin.challengeEndDate" 
  | "admin.saveChallenge" 
  | "admin.uploadVideo";

// Create specific translation objects
const fr = {
  "admin.title": "Panel d'administration",
  "admin.channels": "Chaînes",
  "admin.users": "Utilisateurs",
  "admin.licenses": "Licences",
  "admin.derivatives": "Dérivés", // Changed from "Pending Approvals" to "Derivatives"
  "admin.manageDerivatives": "Gérer les dérivés",
  "admin.viewDerivative": "Voir le dérivé",
  "admin.approveDerivative": "Approuver",
  "admin.rejectDerivative": "Rejeter",
  "admin.deleteDerivative": "Supprimer",
  "admin.confirmDelete": "Confirmer la suppression",
  "admin.usersPanelTitle": "Gestion des utilisateurs",
  "admin.usersPanelDescription": "Gérer les comptes utilisateurs et leurs rôles.",
  "admin.channelsPanelTitle": "Gestion des chaînes",
  "admin.channelsPanelDescription": "Gérer les chaînes et leurs paramètres.",
  "admin.licensesPanelTitle": "Gestion des licences",
  "admin.licensesPanelDescription": "Gérer les licences et les abonnements.",
  "admin.challengesPanelTitle": "Gestion des défis",
  "admin.challengesPanelDescription": "Gérer les défis et leurs paramètres.",
  "admin.analyticsPanelTitle": "Tableau de bord analytique",
  "admin.analyticsPanelDescription": "Consulter les statistiques et les performances de la plateforme.",
  "admin.uploadClip": "Télécharger un clip",
  "admin.editChannel": "Modifier la chaîne",
  "admin.createChannel": "Créer une chaîne",
  "admin.channelName": "Nom de la chaîne",
  "admin.channelDescription": "Description de la chaîne",
  "admin.channelLogo": "Logo de la chaîne",
  "admin.channelWebsite": "Site web de la chaîne",
  "admin.channelCategory": "Catégorie de la chaîne",
  "admin.saveChanges": "Enregistrer les modifications",
  "admin.cancel": "Annuler",
  "admin.deleteChannel": "Supprimer la chaîne",
  "admin.confirmChannelDeletion": "Êtes-vous sûr de vouloir supprimer cette chaîne ? Cette action est irréversible.",
  "admin.userManagement": "Gestion des utilisateurs",
  "admin.createUser": "Créer un utilisateur",
  "admin.editUser": "Modifier l'utilisateur",
  "admin.deleteUser": "Supprimer l'utilisateur",
  "admin.confirmUserDeletion": "Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.",
  "admin.userName": "Nom d'utilisateur",
  "admin.userEmail": "Adresse e-mail",
  "admin.userRole": "Rôle de l'utilisateur",
  "admin.saveUser": "Enregistrer l'utilisateur",
  "admin.licenseManagement": "Gestion des licences",
  "admin.createLicense": "Créer une licence",
  "admin.editLicense": "Modifier la licence",
  "admin.deleteLicense": "Supprimer la licence",
  "admin.confirmLicenseDeletion": "Êtes-vous sûr de vouloir supprimer cette licence ? Cette action est irréversible.",
  "admin.licenseName": "Nom de la licence",
  "admin.licenseDescription": "Description de la licence",
  "admin.licensePrice": "Prix de la licence",
  "admin.saveLicense": "Enregistrer la licence",
  "admin.challengeManagement": "Gestion des défis",
  "admin.createChallenge": "Créer un défi",
  "admin.editChallenge": "Modifier le défi",
  "admin.deleteChallenge": "Supprimer le défi",
  "admin.confirmChallengeDeletion": "Êtes-vous sûr de vouloir supprimer ce défi ? Cette action est irréversible.",
  "admin.challengeName": "Nom du défi",
  "admin.challengeDescription": "Description du défi",
  "admin.challengeStartDate": "Date de début du défi",
  "admin.challengeEndDate": "Date de fin du défi",
  "admin.saveChallenge": "Enregistrer le défi",
  "admin.uploadVideo": "Téléverser une vidéo",
};

const en = {
  "admin.title": "Admin Panel",
  "admin.channels": "Channels",
  "admin.users": "Users",
  "admin.licenses": "Licenses",
  "admin.derivatives": "Derivatives", // Changed from "Pending Approvals" to "Derivatives"
  "admin.manageDerivatives": "Manage Derivatives",
  "admin.viewDerivative": "View Derivative",
  "admin.approveDerivative": "Approve",
  "admin.rejectDerivative": "Reject",
  "admin.deleteDerivative": "Delete",
  "admin.confirmDelete": "Confirm Deletion",
  "admin.usersPanelTitle": "User Management",
  "admin.usersPanelDescription": "Manage user accounts and their roles.",
  "admin.channelsPanelTitle": "Channel Management",
  "admin.channelsPanelDescription": "Manage channels and their settings.",
  "admin.licensesPanelTitle": "License Management",
  "admin.licensesPanelDescription": "Manage licenses and subscriptions.",
  "admin.challengesPanelTitle": "Challenge Management",
  "admin.challengesPanelDescription": "Manage challenges and their settings.",
  "admin.analyticsPanelTitle": "Analytics Dashboard",
  "admin.analyticsPanelDescription": "View platform statistics and performance metrics.",
  "admin.uploadClip": "Upload Clip",
  "admin.editChannel": "Edit Channel",
  "admin.createChannel": "Create Channel",
  "admin.channelName": "Channel Name",
  "admin.channelDescription": "Channel Description",
  "admin.channelLogo": "Channel Logo",
  "admin.channelWebsite": "Channel Website",
  "admin.channelCategory": "Channel Category",
  "admin.saveChanges": "Save Changes",
  "admin.cancel": "Cancel",
  "admin.deleteChannel": "Delete Channel",
  "admin.confirmChannelDeletion": "Are you sure you want to delete this channel? This action is irreversible.",
  "admin.userManagement": "User Management",
  "admin.createUser": "Create User",
  "admin.editUser": "Edit User",
  "admin.deleteUser": "Delete User",
  "admin.confirmUserDeletion": "Are you sure you want to delete this user? This action is irreversible.",
  "admin.userName": "User Name",
  "admin.userEmail": "Email Address",
  "admin.userRole": "User Role",
  "admin.saveUser": "Save User",
  "admin.licenseManagement": "License Management",
  "admin.createLicense": "Create License",
  "admin.editLicense": "Edit License",
  "admin.deleteLicense": "Delete License",
  "admin.confirmLicenseDeletion": "Are you sure you want to delete this license? This action is irreversible.",
  "admin.licenseName": "License Name",
  "admin.licenseDescription": "License Description",
  "admin.licensePrice": "License Price",
  "admin.saveLicense": "Save License",
  "admin.challengeManagement": "Challenge Management",
  "admin.createChallenge": "Create Challenge",
  "admin.editChallenge": "Edit Challenge",
  "admin.deleteChallenge": "Delete Challenge",
  "admin.confirmChallengeDeletion": "Are you sure you want to delete this challenge? This action is irreversible.",
  "admin.challengeName": "Challenge Name",
  "admin.challengeDescription": "Challenge Description",
  "admin.challengeStartDate": "Challenge Start Date",
  "admin.challengeEndDate": "Challenge End Date",
  "admin.saveChallenge": "Save Challenge",
  "admin.uploadVideo": "Upload Video",
};

// Export the translation objects
export const adminTranslations = {
  fr,
  en
};

// Re-export individual language objects for backward compatibility
export { fr, en };
